// Author: Tejal Komb

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { FaUsers, FaUpload, FaClipboardList, FaBriefcase, FaBars, FaMoneyBillAlt } from 'react-icons/fa';
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Input,
    useToast,
    Heading,
    Icon,
    VStack,
    Text,
    Switch,
    Select,
} from '@chakra-ui/react';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import axios from 'axios';
import Logo from "../images/AiM4u Registered Logo (1).png";

const ViewApplications = () => <Heading textAlign="center">View Applications Page</Heading>;
const InternshipDetails = () => <Heading textAlign="center">Internship Details Page</Heading>;

const UploadCSV = ({ handleFileChange, handleSubmit, isUploading }) => (
    <Box bg="white" p={6} boxShadow="md" borderRadius="md">
        <Flex align="center" justify="center" direction="column" mb={4}>
            <Icon as={FaUpload} boxSize={10} color="blue.500" mb={2} />
            <Heading size="sm" color="blue.700">Upload Internship Details via CSV</Heading>
            <Text fontSize="sm" color="gray.600" textAlign="center">Easily add multiple internships at once.</Text>
        </Flex>
        <FormControl>
            <FormLabel htmlFor="csv-upload" fontSize="sm" color="gray.700">Select CSV File</FormLabel>
            <Input
                id="csv-upload"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                size="sm"
            />
        </FormControl>
        <Button
            mt={4}
            colorScheme="blue"
            size="sm"
            onClick={handleSubmit}
            isLoading={isUploading}
            loadingText="Uploading..."
            w="full"
        >
            Upload CSV
        </Button>
    </Box>
);

const AddInternshipForm = () => {
    const [title, setTitle] = useState('');
    const [Internship_type, setType] = useState('');
    const [keySkills, setKeySkills] = useState('');
    const [duration, setDuration] = useState('');
    const [fees, setFees] = useState('');
    const [completionCertificate, setCompletionCertificate] = useState(false);
    const [LOR, setLOR] = useState(false);
    const [paperWriting, setPaperWriting] = useState(false);
    const [projectCount, setProjectCount] = useState('');
    const toast = useToast();
    const [eligibility, setEligibility] = useState('');

    const addInternship = async () => {
        try {
            const accessToken = window.sessionStorage.getItem('accessToken');
            await axios.post('https://gyanbackend.aim4u.co.in/super/add-internship/', {
                title,
                Internship_type,
                key_skills: keySkills,
                duration,
                fees,
                completion_certificate: completionCertificate,
                lor: LOR,
                paper_writing: paperWriting,
                project_count: projectCount,
                eligibility: eligibility,
            }, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });
            toast({
                title: 'Internship Added',
                description: 'The new internship has been added successfully.',
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
            setTitle('');
            setType('');
            setKeySkills('');
            setDuration('');
            setFees('');
            setCompletionCertificate(false);
            setLOR(false);
            setPaperWriting(false);
            setProjectCount('');
            setEligibility('');
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to add internship.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        }
    };

    return (
        <Box bg="white" p={6} boxShadow="md" borderRadius="md">
            <Heading size="sm" mb={4} color="teal.700" textAlign="center">Manually Add New Internship</Heading>
            <FormControl mb={3}>
                <FormLabel htmlFor="title" fontSize="sm" color="gray.700">Title</FormLabel>
                <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Enter title" size="sm" />
            </FormControl>
            <FormControl mb={3}>
                <FormLabel htmlFor="eligibility" fontSize="sm" color="gray.700">Eligibility Criteria</FormLabel>
                <Input id="eligibility" value={eligibility} onChange={(e) => setEligibility(e.target.value)} placeholder="e.g., B.Tech/BE Computer Science" size="sm" />
            </FormControl>
            <FormControl mb={3}>
                <FormLabel htmlFor="InternshipType" fontSize="sm" color="gray.700">Internship Type</FormLabel>
                <Select id="InternshipType" value={Internship_type} onChange={(e) => setType(e.target.value)} size="sm">
                    <option value="">Select Type</option>
                    <option value="Remote">Remote</option>
                    <option value="On-Site">On-Site</option>
                    <option value="Hybrid">Hybrid</option>
                </Select>
            </FormControl>
            <FormControl mb={3}>
                <FormLabel htmlFor="key-skills" fontSize="sm" color="gray.700">Key Skills</FormLabel>
                <Input id="key-skills" value={keySkills} onChange={(e) => setKeySkills(e.target.value)} placeholder="Enter key skills" size="sm" />
            </FormControl>
            <FormControl mb={3}>
                <FormLabel htmlFor="duration" fontSize="sm" color="gray.700">Duration (weeks)</FormLabel>
                <Input id="duration" type="number" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="Enter duration" size="sm" />
            </FormControl>
            <FormControl mb={3}>
                <FormLabel htmlFor="fees" fontSize="sm" color="gray.700">Fees</FormLabel>
                <Input id="fees" type="number" value={fees} onChange={(e) => setFees(e.target.value)} placeholder="Enter fees" size="sm" />
            </FormControl>
            <FormControl display="flex" alignItems="center" mb={3}>
                <FormLabel htmlFor="completion-certificate" mb={0} fontSize="sm" color="gray.700">Completion Certificate</FormLabel>
                <Switch id="completion-certificate" isChecked={completionCertificate} onChange={() => setCompletionCertificate(!completionCertificate)} size="sm" ml={2} />
            </FormControl>
            <FormControl display="flex" alignItems="center" mb={3}>
                <FormLabel htmlFor="lor" mb={0} fontSize="sm" color="gray.700">LOR</FormLabel>
                <Switch id="lor" isChecked={LOR} onChange={() => setLOR(!LOR)} size="sm" ml={2} />
            </FormControl>
            <FormControl display="flex" alignItems="center" mb={3}>
                <FormLabel htmlFor="paper-writing" mb={0} fontSize="sm" color="gray.700">Paper Writing</FormLabel>
                <Switch id="paper-writing" isChecked={paperWriting} onChange={() => setPaperWriting(!paperWriting)} size="sm" ml={2} />
            </FormControl>
            <FormControl mb={3}>
                <FormLabel htmlFor="project-count" fontSize="sm" color="gray.700">Project Count</FormLabel>
                <Input id="project-count" type="number" value={projectCount} onChange={(e) => setProjectCount(e.target.value)} placeholder="Enter project count" size="sm" />
            </FormControl>
            <Button leftIcon={<FaBriefcase />} colorScheme="teal" w="full" mt={4} size="sm" onClick={addInternship}>
                Add Internship
            </Button>
        </Box>
    );
};

const Admin = () => {
    const [CSV, setCSV] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [showUploadCSV, setShowUploadCSV] = useState(true);
    const toast = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const { isAdmin } = useSelector((state) => state.user);

    useEffect(() => {
        
        if (location.pathname === '/admin') {

        } else {

            setShowUploadCSV(false);
        }
    }, [location]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCSV(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!CSV) {
            toast({
                title: 'No File Selected',
                description: 'Please choose a file.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        const formData = new FormData();
        formData.append('file', CSV);

        setIsUploading(true);

        try {
            const accessToken = window.sessionStorage.getItem('accessToken');
            await axios.post('https://gyanbackend.aim4u.co.in/super/upload-csv/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${accessToken}`
                },
            });

            toast({
                title: 'Upload Successful',
                description: 'CSV uploaded successfully!',
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
            setCSV(null);
        } catch (error) {
            toast({
                title: 'Upload Failed',
                description: 'Something went wrong. Try again.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setIsUploading(false);
        }
    };

    const handleTabChange = (tab) => {
        setShowUploadCSV(tab === 'upload');
    };

    return (
        <Box bg="gray.50" minH="100vh">
            <NavBar isAdmin={isAdmin} />
            <Flex>
                <Box
                    w={isSidebarOpen ? "250px" : "60px"}
                    bg="blue.800"
                    color="white"
                    p={4}
                    minH="100vh"
                    transition="width 0.3s"
                    display="flex"
                    flexDirection="column"
                >
                    <Button
                        variant="ghost"
                        colorScheme="whiteAlpha"
                        color="whiteAlpha.900"
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        alignItems="center"
                        justifyContent="center"
                        leftIcon={<Icon as={FaBars} />}
                        mb={6}
                    >
                        {isSidebarOpen && "Menu"}
                    </Button>

                    {isSidebarOpen && (
                        <VStack spacing={4} align="stretch">
                            <Button
                                leftIcon={<FaClipboardList />}
                                colorScheme="whiteAlpha"
                                color="whiteAlpha.900"
                                onClick={() => navigate('/admin/applications')}
                            >
                                View Applications
                            </Button>
                            <Button
                                leftIcon={<FaMoneyBillAlt />}
                                colorScheme="whiteAlpha"
                                color="whiteAlpha.900"
                                onClick={() => navigate('/admin/transactions')}
                            >
                                View Transactions
                            </Button>
                            <Button
                                leftIcon={<FaBriefcase />}
                                colorScheme="whiteAlpha"
                                color="whiteAlpha.900"
                                onClick={() => navigate('/admin/internshipDetails')}
                            >
                                View Internship Details
                            </Button>
                            <Button
                                leftIcon={<FaUpload />}
                                colorScheme="whiteAlpha"
                                color="whiteAlpha.900"
                                onClick={() => navigate('/admin')}
                            >
                                Add Internship
                            </Button>
                        </VStack>
                    )}
                </Box>

                <Box flex={1} p={6} overflowY="auto">
                    {location.pathname === '/admin' && (
                        <Box maxWidth="md" mx="auto">
                            <Flex bg="white" boxShadow="sm" borderRadius="md" mb={4}>
                                <Button
                                    flex={1}
                                    borderBottom={showUploadCSV ? "2px solid blue.500" : "none"}
                                    bg={showUploadCSV ? "blue.50" : "transparent"}
                                    color={showUploadCSV ? "blue.700" : "gray.600"}
                                    fontWeight={showUploadCSV ? "semibold" : "normal"}
                                    borderRadius="md"
                                    onClick={() => handleTabChange('upload')}
                                    _hover={{ bg: "blue.100" }}
                                    _active={{ bg: "blue.200" }}
                                >
                                    Upload CSV
                                </Button>
                                <Button
                                    flex={1}
                                    borderBottom={!showUploadCSV ? "2px solid teal.500" : "none"}
                                    bg={!showUploadCSV ? "teal.50" : "transparent"}
                                    color={!showUploadCSV ? "teal.700" : "gray.600"}
                                    fontWeight={!showUploadCSV ? "semibold" : "normal"}
                                    borderRadius="md"
                                    onClick={() => handleTabChange('manual')}
                                    _hover={{ bg: "teal.100" }}
                                    _active={{ bg: "teal.200" }}
                                >
                                    Manual Entry
                                </Button>
                            </Flex>
                            {showUploadCSV ? (
                                <UploadCSV
                                    handleFileChange={handleFileChange}
                                    handleSubmit={handleSubmit}
                                    isUploading={isUploading}
                                />
                            ) : (
                                <AddInternshipForm />
                            )}
                        </Box>
                    )}
                    {location.pathname !== '/admin' && <Outlet />}
                </Box>
            </Flex>
        </Box>
    );
};

export default Admin;